import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  clearOwnerEventsError,
  createOwnerEvent,
  fetchOwnerEvent,
  fetchOwnerEvents,
  updateOwnerEvent,
  selectOwnerEventById,
} from '@main/features/events/ownerEventsSlice';
import { UiButton, UiInput, UiLinkButton, UiNativeSelect, UiRadio, UiTextarea } from '@shared/common/ui';

function safeJsonArray(text, fallback) {
  if (text == null || String(text).trim() === '') return fallback;
  try {
    const v = JSON.parse(String(text));
    return Array.isArray(v) ? v : fallback;
  } catch {
    throw new Error('Некорректный JSON в поле массива');
  }
}

const STATUS_OPTIONS = ['active', 'wait'];

export function OwnerEventFormPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew = !eventId || eventId === 'new';
  const existing = useSelector(selectOwnerEventById(eventId));
  const loadError = useSelector((s) => s.ownerEvents.error);
  const [serverError, setServerError] = useState(null);

  const defaultValues = useMemo(
    () => ({
      name: '',
      dateStart: '',
      status: 'active',
      hallType: 'freeSeat',
      image: '',
      place: '',
      address: '',
      maxCountSeats: '',
      pricesJson: '[]',
      promoDiscount: '300',
      promoCodesJson: '[]',
    }),
    [],
  );

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues });

  const hallType = watch('hallType');

  useEffect(() => {
    dispatch(clearOwnerEventsError());
  }, [dispatch]);

  useEffect(() => {
    if (isNew) return;
    if (!existing) {
      dispatch(fetchOwnerEvent(eventId));
    } else {
      reset({
        name: existing.name ?? '',
        dateStart: existing.dateStart ?? '',
        status: existing.status ?? 'active',
        hallType: existing.hallType === 'DK' ? 'DK' : 'freeSeat',
        image: existing.image ?? '',
        place: existing.place ?? '',
        address: existing.address ?? '',
        maxCountSeats:
          existing.maxCountSeats != null && existing.maxCountSeats !== ''
            ? String(existing.maxCountSeats)
            : '',
        pricesJson: JSON.stringify(existing.prices ?? [], null, 2),
        promoDiscount:
          existing.promoDiscount != null && existing.promoDiscount !== ''
            ? String(existing.promoDiscount)
            : '300',
        promoCodesJson: JSON.stringify(existing.promoCodes ?? [], null, 2),
      });
    }
  }, [dispatch, isNew, eventId, existing, reset]);

  const onSubmit = async (values) => {
    setServerError(null);
    let prices;
    let promoCodes;
    try {
      prices = safeJsonArray(values.pricesJson, []);
      promoCodes = safeJsonArray(values.promoCodesJson, []);
    } catch (e) {
      setServerError(e.message);
      return;
    }

    const maxRaw = values.maxCountSeats?.trim();
    const maxParsed = maxRaw === '' ? NaN : Number.parseInt(maxRaw, 10);
    const promoDiscountRaw = values.promoDiscount?.trim();
    const promoDiscountParsed =
      promoDiscountRaw === '' ? 300 : Number.parseInt(promoDiscountRaw, 10);
    if (!Number.isFinite(promoDiscountParsed) || promoDiscountParsed < 1) {
      setServerError('Укажите сумму скидки по промокоду (не меньше 1)');
      return;
    }

    const base = {
      name: values.name.trim(),
      dateStart: values.dateStart.trim(),
      status: values.status.trim(),
      image: values.image.trim(),
      place: values.place.trim(),
      promoDiscount: promoDiscountParsed,
      promoCodes,
      prices,
    };

    let payload;
    if (values.hallType === 'freeSeat') {
      if (!Number.isFinite(maxParsed) || maxParsed < 1) {
        setServerError('Для свободной посадки укажите число мест (не меньше 1)');
        return;
      }
      payload = {
        ...base,
        hallType: 'freeSeat',
        maxCountSeats: maxParsed,
        address: values.address?.trim() ? values.address.trim() : null,
      };
    } else {
      payload = {
        ...base,
        hallType: 'DK',
        address: values.address?.trim() ? values.address.trim() : null,
      };
      if (Number.isFinite(maxParsed) && maxParsed >= 1) {
        payload.maxCountSeats = maxParsed;
      } else {
        payload.maxCountSeats = null;
      }
    }

    const thunk = isNew ? createOwnerEvent(payload) : updateOwnerEvent({ eventId, ...payload });
    const result = await dispatch(thunk);
    if (result.meta.requestStatus === 'fulfilled') {
      await dispatch(fetchOwnerEvents());
      navigate('/profile/events');
    } else {
      setServerError(result.payload ?? 'Ошибка сохранения');
    }
  };

  return (
    <section>
      <div className="page-header">
        <h1>{isNew ? 'Новое событие' : 'Редактирование события'}</h1>
      </div>

      <div className="form-card">
        {!isNew && !existing && loadError && (
          <div className="banner banner--danger" style={{ marginBottom: 16 }}>
            {loadError}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-grid">
            <UiInput
              label="Название"
              style={{ gridColumn: '1 / -1' }}
              type="text"
              error={errors.name?.message}
              {...register('name', { required: 'Укажите название', maxLength: 500 })}
            />
            <UiInput
              label="Дата и время начала"
              type="text"
              placeholder="26.04.2026 19:00"
              error={errors.dateStart?.message}
              {...register('dateStart', { required: 'Укажите дату', maxLength: 80 })}
            />
            <UiNativeSelect label="Статус" {...register('status', { required: true })}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
            </UiNativeSelect>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <span>Тип зала</span>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 8 }}>
                <UiRadio value="freeSeat" {...register('hallType')} label="Свободная посадка (freeSeat)" />
                <UiRadio value="DK" {...register('hallType')} label="По местам (DK)" />
              </div>
            </div>
            <UiInput
              label="Ссылка на изображение"
              style={{ gridColumn: '1 / -1' }}
              type="text"
              placeholder="https://…"
              {...register('image', { maxLength: 5000 })}
            />
            <UiInput
              label="Место проведения"
              style={{ gridColumn: '1 / -1' }}
              type="text"
              error={errors.place?.message}
              {...register('place', { required: 'Укажите место', maxLength: 500 })}
            />
            <UiInput
              label={hallType === 'freeSeat' ? 'Число мест (обязательно)' : 'Число мест (опционально)'}
              type="number"
              min={1}
              error={errors.maxCountSeats?.message}
              {...register('maxCountSeats', {
                validate: (v, formValues) => {
                  if (formValues.hallType !== 'freeSeat') return true;
                  const n = Number.parseInt(String(v ?? '').trim(), 10);
                  if (!Number.isFinite(n) || n < 1) return 'Укажите число ≥ 1';
                  return true;
                },
              })}
            />
            <UiInput
              label={`Адрес ${hallType === 'freeSeat' ? '' : '(опционально)'}`}
              style={{ gridColumn: '1 / -1' }}
              type="text"
              {...register('address', { maxLength: 1000 })}
            />
            <UiInput
              label="Сумма скидки по промокоду, ₽"
              type="number"
              min={1}
              error={errors.promoDiscount?.message}
              {...register('promoDiscount', {
                required: 'Укажите сумму скидки',
                validate: (v) => {
                  const n = Number.parseInt(String(v ?? '').trim(), 10);
                  if (!Number.isFinite(n) || n < 1) return 'Укажите число ≥ 1';
                  return true;
                },
              })}
            />
            <UiTextarea
              label="Цены (JSON-массив)"
              style={{ gridColumn: '1 / -1' }}
              rows={5}
              textareaClassName="ui-control--mono"
              {...register('pricesJson')}
            />
            <UiTextarea
              label="Промокоды (JSON-массив)"
              style={{ gridColumn: '1 / -1' }}
              rows={4}
              textareaClassName="ui-control--mono"
              {...register('promoCodesJson')}
            />
          </div>

          {serverError && <div className="banner banner--danger">{serverError}</div>}

          <div className="form-actions">
            <UiLinkButton to="/profile/events" variant="ghost">
              Отмена
            </UiLinkButton>
            <UiButton type="submit"  disabled={isSubmitting}>
              {isSubmitting ? 'Сохраняем…' : isNew ? 'Создать' : 'Сохранить'}
            </UiButton>
          </div>
        </form>
      </div>
    </section>
  );
}
